// components/UpdateSubPortfolioReason.tsx
import React, { useState, useContext, useCallback } from 'react';
import { Textarea, TextInput, Button, Alert } from '@mantine/core';
import { doc, updateDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { MyUserDataContext } from '../contexts';
import { UpdatedDataType } from '../types';
import { useFirestore, } from "../helpers/firebaseContext";

interface UpdateSubPortfolioReasonProps {
    customerId: string;
    kycId: string;
    currentValue?: string;
}

interface ValidationErrors {
    subPortfolioReason?: string;
    reason?: string;
}

const VALIDATION_RULES = {
    MIN_SUB_PORTFOLIO_REASON_LENGTH: 10,
    MIN_REASON_LENGTH: 5,
} as const;

const UpdateSubPortfolioReason: React.FC<UpdateSubPortfolioReasonProps> = ({
    customerId,
    kycId,
    currentValue = ''
}) => {
    const [updating, setUpdating] = useState(false);
    const [subPortfolioReason, setSubPortfolioReason] = useState(currentValue);
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<ValidationErrors>({});
    const db = useFirestore();

    const userData = useContext(MyUserDataContext);

    const validateSubPortfolioReason = useCallback((value: string): string | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return 'Sub Portfolio Reason is required';
        if (trimmed.length < VALIDATION_RULES.MIN_SUB_PORTFOLIO_REASON_LENGTH) {
            return `Sub Portfolio Reason must be at least ${VALIDATION_RULES.MIN_SUB_PORTFOLIO_REASON_LENGTH} characters`;
        }
        return undefined;
    }, []);

    const validateReason = useCallback((value: string): string | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return 'Reason is required';
        if (trimmed.length < VALIDATION_RULES.MIN_REASON_LENGTH) {
            return `Reason must be at least ${VALIDATION_RULES.MIN_REASON_LENGTH} characters`;
        }
        return undefined;
    }, []);

    const validateForm = useCallback((): boolean => {
        const subPortfolioReasonError = validateSubPortfolioReason(subPortfolioReason);
        const reasonError = validateReason(reason);

        const newErrors: ValidationErrors = {
            subPortfolioReason: subPortfolioReasonError,
            reason: reasonError,
        };

        setErrors(newErrors);
        return !subPortfolioReasonError && !reasonError;
    }, [subPortfolioReason, reason, validateSubPortfolioReason, validateReason]);

    const isFormValid = useCallback((): boolean => {
        return subPortfolioReason.trim().length >= VALIDATION_RULES.MIN_SUB_PORTFOLIO_REASON_LENGTH &&
            reason.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH;
    }, [subPortfolioReason, reason]);

    const handleSubPortfolioReasonChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.currentTarget.value;
        setSubPortfolioReason(value);

        if (value.trim().length >= VALIDATION_RULES.MIN_SUB_PORTFOLIO_REASON_LENGTH && errors.subPortfolioReason) {
            setErrors(prev => ({ ...prev, subPortfolioReason: undefined }));
        }
    }, [errors.subPortfolioReason]);

    const handleSubPortfolioReasonBlur = useCallback(() => {
        const error = validateSubPortfolioReason(subPortfolioReason);
        if (error) {
            setErrors(prev => ({ ...prev, subPortfolioReason: error }));
        }
    }, [subPortfolioReason, validateSubPortfolioReason]);

    const handleReasonChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setReason(value);

        if (value.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH && errors.reason) {
            setErrors(prev => ({ ...prev, reason: undefined }));
        }
    }, [errors.reason]);

    const handleReasonBlur = useCallback(() => {
        const error = validateReason(reason);
        if (error) {
            setErrors(prev => ({ ...prev, reason: error }));
        }
    }, [reason, validateReason]);

    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;

        setUpdating(true);

        try {
            const trimmedValue = subPortfolioReason.trim();
            const kycDocRef = doc(db, 'customers', customerId, 'kyc', kycId);

            const newEntry: UpdatedDataType = {
                updated_by: userData.user?.email || 'unknown',
                updated_on: Timestamp.now(),
                updated_field_name: ['sub_portfolio_reason'],
                updated_fields: [{ sub_portfolio_reason: trimmedValue }],
                initial_value: [{ sub_portfolio_reason: currentValue }],
                reason: reason.trim()
            };

            await updateDoc(kycDocRef, {
                sub_portfolio_reason: trimmedValue,
                updated_data: arrayUnion(newEntry)
            });

            notifications.show({
                title: 'Success!',
                message: 'Sub Portfolio Reason updated successfully',
                color: 'green',
                icon: <IconCheck size={16} />
            });

            setSubPortfolioReason(trimmedValue);
            setReason('');
            setErrors({});
            modals.closeAll();

        } catch (error) {
            console.error('Error updating sub_portfolio_reason:', error);

            const message = error instanceof Error ? error.message : 'Unknown error occurred';
            notifications.show({
                title: 'Error',
                message: `Failed to update Sub Portfolio Reason: ${message}`,
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setUpdating(false);
        }
    }, [validateForm, userData.user?.email, subPortfolioReason, reason, customerId, kycId, currentValue, db]);

    const hasErrors = Boolean(errors.subPortfolioReason || errors.reason);
    const formStatus = isFormValid();

    return (
        <>
            <Textarea
                label="Enter Sub Portfolio Reason"
                data-autofocus
                value={subPortfolioReason}
                onChange={handleSubPortfolioReasonChange}
                onBlur={handleSubPortfolioReasonBlur}
                error={errors.subPortfolioReason}
                placeholder="Please provide detailed reason for sub portfolio..."
                required
                minRows={3}
                autosize
            />

            <TextInput
                label="Reason for Update"
                value={reason}
                onChange={handleReasonChange}
                onBlur={handleReasonBlur}
                error={errors.reason}
                placeholder={`Please provide a detailed reason (minimum ${VALIDATION_RULES.MIN_REASON_LENGTH} characters)...`}
                required
                mt="md"
            />

            <Button
                loading={updating}
                fullWidth
                disabled={updating || !formStatus}
                onClick={handleSubmit}
                mt="md"
            >
                Submit
            </Button>

            {hasErrors && (
                <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Please fix the following errors:"
                    color="red"
                    mt="sm"
                >
                    {errors.subPortfolioReason && <div>• {errors.subPortfolioReason}</div>}
                    {errors.reason && <div>• {errors.reason}</div>}
                </Alert>
            )}

            {!updating && (
                <Alert
                    color={formStatus ? 'green' : 'gray'}
                    variant="light"
                    mt="xs"
                    style={{ fontSize: '12px' }}
                >
                    Form is {formStatus ? 'ready to submit' : 'incomplete'}
                </Alert>
            )}
        </>
    );
};

export default UpdateSubPortfolioReason;